import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getAxiosApiV2WithAuthorization,
  openFromUrlInNewTab,
  useDeepCompareMemo,
} from '@clatter/platform';
import { config } from '../../config';
import { useGetJobStatusQuery, useGetJobZipPresignedUrlMutation } from '../../services';
import { addNotification, longPullUISelectors, updateLongPullUIItem } from '../../store';
import { setDocumentsButtonsEnabled } from '../../store/UI/projectStepper.slice';

interface LongPullDocumentBuildQueueProps {
  onComplete?: (data: any) => void;
}

const QueueItem: FC<{ item: ILongPullUIItem; onComplete?: (data: any) => void }> = ({
  item,
  onComplete,
}) => {
  const [stopPolling, setStopPolling] = useState(false);
  const dispatch = useDispatch();
  const downloadTriggeredRef = useRef(false);

  const { id } = item || {};
  const [getPresignedUrl] = useGetJobZipPresignedUrlMutation();

  const url = useDeepCompareMemo(() => {
    switch (item?.jobType) {
      case 'zip':
        return `${config.assetServiceBaseUrl}/zip-build-jobs/${id}`;
      default:
        return `${config.assetServiceBaseUrl}/document-build-jobs/${id}`;
    }
  }, [item, config.assetServiceBaseUrl]);

  const { data: longPullData, isError } = useGetJobStatusQuery(
    { url },
    {
      skip: stopPolling || item?.isCompleted, // 🚀 don’t poll completed jobs
      pollingInterval: config.defaultFetchInterval,
    },
  );

  useEffect(() => {
    // 🚀 Guard: don’t process if already completed or polling stopped
    if (item?.isCompleted || stopPolling) return;
    if (!longPullData) return;

    const generateAssets = async (buildJobId: any) => {
      try {
        await getAxiosApiV2WithAuthorization({
          url: `${config.assetServiceBaseUrl}/document-build-jobs/${buildJobId}/generate-documents`,
          method: 'POST',
          data: {},
        });
      } catch {
        console.error('Error triggering document generate');
      }
    };

    let message = '';
    switch (item?.jobType) {
      case 'zip':
        if (!longPullData?.tasks_total_count) {
          message = `Preparing files for zipping...`;
          return;
        }
        message = `Zipping ${longPullData?.tasks_completed_count} / ${longPullData?.tasks_total_count} files.`;
        break;
      default:
        message = `Generated ${longPullData?.tasks_completed_count} of ${longPullData?.tasks_total_count} document(s)`;
    }

    const updates: Partial<ILongPullUIItem> = {
      message,
      completedFiles: longPullData?.tasks_completed_count,
      totalFiles: longPullData?.tasks_total_count || item?.totalFiles,
    };

    const handleCompletion = async (isError?: boolean) => {
      if (downloadTriggeredRef.current) return;
      downloadTriggeredRef.current = true;

      if (item?.jobType === 'zip' && !isError) {
        try {
          const response = await getPresignedUrl({ longpullId: +item.id }).unwrap();

          if (response.data) {
            openFromUrlInNewTab(response.data);
          } else {
            dispatch(
              addNotification({
                type: 'error',
                message: 'Error: No presigned URL received for zip download!',
              }),
            );
          }
        } catch (err) {
          dispatch(addNotification({ type: 'error', message: 'Download failed!' }));
          console.error('Download failed!', err);
        }
      }

      if (item?.jobType !== 'zip') {
        dispatch(setDocumentsButtonsEnabled(true));
      }

      onComplete?.(longPullData);
      setStopPolling(true);
    };

    if (longPullData?.status === 'success' || longPullData?.status === 'completed') {
      updates.isCompleted = true;
      updates.type = 'success';
      handleCompletion();
      dispatch(updateLongPullUIItem({ itemId: id, updates }));

      //@ts-ignore
      if (longPullData?.output_type === 'folder' || longPullData.output_type === 'zip') {
        generateAssets(id);
      }
      return;
    }

    if (longPullData?.status === 'error' || longPullData?.status === 'errored') {
      updates.message =
        item?.jobType === 'zip'
          ? `Error zipping files: ${longPullData?.details?.[0]}` || 'Error zipping files.'
          : 'Error generating documents';
      updates.type = 'error';
      updates.isCompleted = true;
      handleCompletion(true);
      dispatch(updateLongPullUIItem({ itemId: id, updates }));
      return;
    }

    dispatch(updateLongPullUIItem({ itemId: id, updates }));
  }, [longPullData, stopPolling, dispatch, id, item, onComplete]);

  useEffect(() => {
    if (item?.isCompleted) return; // 🚀 Guard
    if (isError && !stopPolling) {
      setStopPolling(true);
      dispatch(
        updateLongPullUIItem({
          itemId: id,
          updates: {
            message:
              item?.jobType === 'zip'
                ? 'Error zipping files.'
                : 'Error generating documents: Server error.',
            type: 'error',
            isCompleted: true,
          },
        }),
      );
    }
  }, [isError, stopPolling, item, id, dispatch]);

  return null;
};

export const LongPullDocumentBuildQueue: FC<LongPullDocumentBuildQueueProps> = ({ onComplete }) => {
  const queue = useSelector((state: any) => longPullUISelectors.getQueueByKey(state, 'global'));

  return (
    <>
      {(queue || []).map((item) => (
        <QueueItem key={item.id} item={item} onComplete={onComplete} />
      ))}
    </>
  );
};
